import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import { getAuthToken } from '../utils/auth';

// Popular CRM platforms used by RV dealerships
const CRM_PLATFORMS = [
  {
    name: 'VinSolutions',
    url: 'https://www.vinsolutions.com',
    description: 'Automotive CRM & lead management',
    logo: 'https://logo.clearbit.com/vinsolutions.com',
    color: '#0066CC'
  },
  {
    name: 'DealerSocket',
    url: 'https://www.dealersocket.com',
    description: 'Dealer management & CRM software',
    logo: 'https://logo.clearbit.com/dealersocket.com',
    color: '#00A651'
  },
  {
    name: 'Salesforce',
    url: 'https://www.salesforce.com',
    description: 'World\'s #1 CRM platform',
    logo: 'https://logo.clearbit.com/salesforce.com',
    color: '#00A1E0'
  },
  {
    name: 'HubSpot',
    url: 'https://www.hubspot.com',
    description: 'Free CRM & marketing automation',
    logo: 'https://logo.clearbit.com/hubspot.com',
    color: '#FF7A59'
  },
  {
    name: 'Zoho CRM',
    url: 'https://www.zoho.com/crm',
    description: 'Affordable CRM for all business sizes',
    logo: 'https://logo.clearbit.com/zoho.com',
    color: '#E42527'
  },
  {
    name: 'DealerCenter',
    url: 'https://www.dealercenter.com',
    description: 'Dealer management system',
    logo: 'https://logo.clearbit.com/dealercenter.com',
    color: '#1A365D'
  },
  {
    name: 'AutoRaptor',
    url: 'https://www.autoraptor.com',
    description: 'Auto dealer CRM software',
    logo: 'https://logo.clearbit.com/autoraptor.com',
    color: '#FF4500'
  },
  {
    name: 'elead CRM',
    url: 'https://www.eleadcrm.com',
    description: 'Automotive CRM & digital retailing',
    logo: 'https://logo.clearbit.com/eleadcrm.com',
    color: '#003366'
  },
  {
    name: 'Pipedrive',
    url: 'https://www.pipedrive.com',
    description: 'Sales-focused CRM',
    logo: 'https://logo.clearbit.com/pipedrive.com',
    color: '#017737'
  },
  {
    name: 'Monday.com',
    url: 'https://www.monday.com',
    description: 'Work OS & CRM platform',
    logo: 'https://logo.clearbit.com/monday.com',
    color: '#FF3D57'
  },
  {
    name: 'Freshsales',
    url: 'https://www.freshworks.com/crm/sales',
    description: 'AI-powered sales CRM',
    logo: 'https://logo.clearbit.com/freshworks.com',
    color: '#F26522'
  },
  {
    name: 'Copper',
    url: 'https://www.copper.com',
    description: 'CRM for Google Workspace',
    logo: 'https://logo.clearbit.com/copper.com',
    color: '#F7941D'
  }
];

const CRMHub = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const token = getAuthToken();
    if (!token) navigate('/auth');
  }, [navigate]);

  const openCRM = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} transition-colors duration-200`} data-testid="crm-hub-page">
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-4 py-4 sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/dealer')}
              className={`flex items-center gap-2 ${isDark ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-[#004E86] hover:bg-gray-100'}`}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Button>
            <div className={`hidden sm:block h-6 w-px ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
            <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-[#004E86]'}`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              CRM Hub
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Intro Section */}
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border p-6 mb-8 shadow-sm`}>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            Connect with Your Favorite CRM
          </h2>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Access your CRM platform directly from here. Click on any platform below to open it in a new tab. 
            Manage your leads, track sales, and keep your customer relationships organized.
          </p>
        </div>

        {/* CRM Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {CRM_PLATFORMS.map((crm) => (
            <button
              key={crm.name}
              onClick={() => openCRM(crm.url)}
              className={`group ${isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-500 hover:bg-gray-750' : 'bg-white border-gray-200 hover:border-[#004E86] hover:shadow-lg'} 
                border rounded-xl p-4 sm:p-6 transition-all duration-200 text-left flex flex-col items-center`}
              data-testid={`crm-link-${crm.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Logo */}
              <div 
                className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center mb-3 sm:mb-4 transition-transform group-hover:scale-105`}
                style={{ backgroundColor: `${crm.color}15` }}
              >
                <img 
                  src={crm.logo} 
                  alt={`${crm.name} logo`}
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg items-center justify-center text-white font-bold text-lg hidden"
                  style={{ backgroundColor: crm.color }}
                >
                  {crm.name.charAt(0)}
                </div>
              </div>

              {/* Name */}
              <h3 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-1 group-hover:text-[#004E86] transition-colors`}>
                {crm.name}
              </h3>

              {/* Description */}
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} text-center line-clamp-2 hidden sm:block`}>
                {crm.description}
              </p>

              {/* External Link Icon */}
              <div className={`mt-2 sm:mt-3 flex items-center gap-1 text-xs ${isDark ? 'text-gray-500 group-hover:text-gray-300' : 'text-gray-400 group-hover:text-[#004E86]'} transition-colors`}>
                <ExternalLink size={12} />
                <span className="hidden sm:inline">Open</span>
              </div>
            </button>
          ))}
        </div>

        {/* Tips Section */}
        <div className={`${isDark ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'} rounded-xl border p-6 mt-8`}>
          <h3 className={`font-semibold ${isDark ? 'text-blue-300' : 'text-blue-800'} mb-2`}>
            Pro Tip
          </h3>
          <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
            Export your leads from RV Match AI and import them directly into your CRM. 
            Most platforms support CSV imports, making it easy to transfer your qualified leads for follow-up.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CRMHub;
